import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../layout/Header";
import Footer from "../layout/Footer";
import { useSEO } from "../hooks/useSEO";
import {
  listPosts,
  postThumbnail,
  postExcerpt,
  formatBlogDate,
  type BlogPost,
} from "../services/blogger";

export default function Blog() {
  useSEO({
    title: "Blog — Vastavik",
    description:
      "Insights on media authenticity, deepfake detection, and content verification from Vastavik.",
    path: "/blog",
  });

  const navigate = useNavigate();
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [pageToken, setPageToken] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [currentPage, setCurrentPage] = useState(1);
  const loadMoreRef = useRef<HTMLDivElement>(null);

  const load = useCallback(async (token?: string) => {
    try {
      token ? setLoadingMore(true) : setLoading(true);
      setError(null);
      
      const { items, nextPageToken } = await listPosts(token);
      
      setPosts((prev) => (token ? [...prev, ...items] : items));
      setPageToken(nextPageToken);
      if (token) setCurrentPage((prev) => prev + 1);
      
    } catch (e: any) {
      setError(e?.message || "Couldn't load the blog. Please try again.");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const target = entries[0];
        if (target.isIntersecting && !loadingMore && !loading && pageToken) {
          load(pageToken);
        }
      },
      {
        root: null,
        rootMargin: "0px",
        threshold: 0.1, 
      }
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => {
      if (loadMoreRef.current) observer.unobserve(loadMoreRef.current);
    };
  }, [loading, loadingMore, pageToken, load]);

  return (
    <div className="min-h-screen bg-white text-black flex flex-col font-sans">
      <Header />

      <main className="flex-1 w-full pb-32">
        <div className="mx-auto w-full max-w-[1440px] px-5 py-10 md:px-10 md:py-14">
          <header className="mb-12 md:mb-16 flex flex-col items-center text-center">
            <h1 className="blog-page-title">Blogs</h1>
            <p className="blog-page-subtitle mt-2 max-w-[832px]">
              Insights on media authenticity, deepfake detection, and content
              verification from the Vastavik team.
            </p>
          </header>

          {loading ? (
            <div className="flex justify-center py-24">
              <div className="h-9 w-9 animate-spin rounded-full border-2 border-black/15 border-t-black" />
            </div>
          ) : error ? (
            <div className="mx-auto max-w-2xl rounded-xl border border-black/10 bg-black/[0.02] p-8 text-center">
              <p className="text-[15px] text-black/70">{error}</p>
              <button
                onClick={() => load()}
                className="mt-4 rounded-lg border border-black/15 px-4 py-2 text-[14px] transition hover:bg-black/5"
              >
                Try again
              </button>
            </div>
          ) : posts.length === 0 ? (
            <div className="mx-auto max-w-2xl rounded-xl border border-black/10 bg-black/[0.02] p-12 text-center">
              <p className="text-[17px] font-medium">No posts yet</p>
              <p className="mt-2 text-[14px] text-black/55">
                We're working on it — check back soon for articles on media
                authenticity and verification.
              </p>
            </div>
          ) : (
            <>
              {/* Updated Grid Cards matching the Further Reading layout */}
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {posts.map((post) => {
                  const thumb = postThumbnail(post);
                  return (
                    <article
                      key={post.id}
                      onClick={() => navigate(`/blog/${post.id}`)}
                      className="flex flex-col overflow-hidden cursor-pointer hover:shadow-lg transition-shadow"
                      style={{ backgroundColor: "#F9F9F9", border: "1px solid #7E7576" }}
                    >
                      <div className="h-[220px] w-full bg-[#E5E7EB] relative flex items-center justify-center overflow-hidden">
                        {thumb ? (
                          <img
                            src={thumb}
                            alt={post.title}
                            loading="lazy"
                            className="object-cover w-full h-full transition duration-500 hover:scale-105"
                          />
                        ) : (
                          <span className="font-maxwell-regular text-2xl uppercase tracking-wide text-black/20">
                            Vastavik
                          </span>
                        )}
                      </div>
                      <div className="p-6 flex flex-col flex-1">
                        <span className="text-[#5F5E5E] text-[14px] mb-2 font-light">
                          {formatBlogDate(post.published)}
                        </span>
                        <h3 
                          className="text-black mb-3 line-clamp-2"
                          style={{
                            fontFamily: "'Product Sans Medium', sans-serif",
                            fontSize: "18px",
                            lineHeight: "25.2px",
                          }}
                        >
                          {post.title}
                        </h3>
                        <p className="text-[#5F5E5E] text-[15px] leading-relaxed line-clamp-3">
                          {postExcerpt(post)}
                        </p>
                      </div>
                    </article>
                  );
                })}
              </div>

              {/* Scroll target sentinel */}
              <div ref={loadMoreRef} className="h-10 w-full mt-8 flex justify-center items-center">
                {loadingMore && (
                  <div className="h-7 w-7 animate-spin rounded-full border-2 border-black/15 border-t-black" />
                )}
              </div>

              <div className="mt-2 flex items-center justify-end font-sans text-[16px] text-gray-800">
                <span>Page </span>
                <span className="mx-1 font-bold underline underline-offset-4">{currentPage}</span>
                {pageToken ? <span> of ...</span> : <span> of {currentPage}</span>}
              </div>
            </>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}